import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { UUID } from "@/types";

type ColorScheme = "light" | "dark" | "auto";

interface UiState {
  sidebarCollapsed: boolean;
  colorScheme: ColorScheme;
  currentCompanyId: UUID | null;
  currentPropertyId: UUID | null;
  /**
   * Set only when a system admin opens a company they are not a member of.
   * Explicit state (rather than letting any unknown currentCompanyId through)
   * so stale-id validation can still self-heal for everyone else.
   */
  adminJumpCompanyId: UUID | null;
  inboxListWidth: number;
  inboxCustomerWidth: number;

  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setColorScheme: (scheme: ColorScheme) => void;
  setCurrentCompanyId: (companyId: UUID | null) => void;
  setAdminJumpCompanyId: (companyId: UUID | null) => void;
  setCurrentPropertyId: (propertyId: UUID | null) => void;
  setInboxListWidth: (width: number) => void;
  setInboxCustomerWidth: (width: number) => void;
  reset: () => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      colorScheme: "auto",
      currentCompanyId: null,
      currentPropertyId: null,
      adminJumpCompanyId: null,
      inboxListWidth: 320,
      inboxCustomerWidth: 320,

      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setSidebarCollapsed: (collapsed: boolean) =>
        set({ sidebarCollapsed: collapsed }),

      setColorScheme: (scheme: ColorScheme) =>
        set({ colorScheme: scheme }),

      setCurrentCompanyId: (companyId: UUID | null) =>
        set({
          currentCompanyId: companyId,
          currentPropertyId: null,
          adminJumpCompanyId: null,
        }),

      setAdminJumpCompanyId: (companyId: UUID | null) =>
        set({
          adminJumpCompanyId: companyId,
          currentCompanyId: companyId,
          currentPropertyId: null,
        }),

      setCurrentPropertyId: (propertyId: UUID | null) =>
        set({ currentPropertyId: propertyId }),

      setInboxListWidth: (width: number) =>
        set({ inboxListWidth: width }),

      setInboxCustomerWidth: (width: number) =>
        set({ inboxCustomerWidth: width }),

      reset: () =>
        set({
          sidebarCollapsed: false,
          currentCompanyId: null,
          currentPropertyId: null,
          adminJumpCompanyId: null,
        }),
    }),
    {
      name: "helppilot-ui",
      partialize: (state) => ({
        colorScheme: state.colorScheme,
        currentCompanyId: state.currentCompanyId,
        currentPropertyId: state.currentPropertyId,
        adminJumpCompanyId: state.adminJumpCompanyId,
        inboxListWidth: state.inboxListWidth,
        inboxCustomerWidth: state.inboxCustomerWidth,
      }),
    },
  ),
);
